import {
  Badge,
  Box,
  CircularProgress,
  CircularProgressLabel,
  Divider,
  Flex,
  Grid,
  GridItem,
  Image,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Text,
  useDisclosure,
  VStack
} from '@chakra-ui/react'
import { Chart, registerables } from 'chart.js'
import { useEffect, useState } from 'react'
import { Bar } from 'react-chartjs-2'
import { Link, useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { DetailField } from '../../../components/DetailField'
import { DetailHero, HeroButton, HeroLinkButton } from '../../../components/DetailHero'
import { EmptyCell } from '../../../components/EmptyCell'
import { Icon } from '../../../components/Icon'
import { RevenueSummaryCards } from '../../../components/revenueSummary/RevenueSummaryCards'
import { SectionCard, SectionTitle, StatCard } from '../../../components/SectionCard'
import { SearchableSelect } from '../../../components/Select'
import { StatusBadge } from '../../../components/StatusBadge'
import {
  contestPublicUrl,
  formatApiDate,
  formatCurrency,
  formatWithdrawWindow,
  useAppToast,
  useDownload,
  useHasRole,
  WEBSITE_URL
} from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import { IContestApprovedArtwork, IContestDetail, IContestForSelect } from '../../../services/Contests/interfaces'
import { magenta } from '../../../theme'
import { resolveArtMeta } from './artFieldHelpers'
import ArtMetaInline from './ArtMetaInline'

Chart.register(...registerables)

const contestsService = new ContestsService()

function getContestStatus(contest: IContestDetail): { label: string; colorScheme: string } {
  const now = new Date()
  const registrationDeadline = new Date(contest.registration_deadline)
  const submissionDeadline = new Date(contest.submission_deadline)

  if (now < registrationDeadline) {
    return { label: 'Inscrições abertas', colorScheme: 'green' }
  }
  if (now < submissionDeadline) {
    return { label: 'Envio de obras', colorScheme: 'blue' }
  }
  return { label: 'Encerrado', colorScheme: 'gray' }
}

export default function ContestDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const hasRole = useHasRole()
  const toast = useAppToast()
  const { isDownloading, download } = useDownload()
  const [contest, setContest] = useState<IContestDetail | null>(null)
  const [artworks, setArtworks] = useState<IContestApprovedArtwork[]>([])
  const [contestOptions, setContestOptions] = useState<IContestForSelect[]>([])
  const [loading, setLoading] = useState(true)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const imageModal = useDisclosure()

  function openImagePreview(url: string) {
    setPreviewUrl(url)
    imageModal.onOpen()
  }

  useEffect(() => {
    contestsService
      .listForSelect()
      .then((data) => setContestOptions(data))
      .catch(() => undefined)
  }, [])

  useEffect(() => {
    setLoading(true)
    setContest(null)
    setArtworks([])

    contestsService
      .getDetailBySlug(slug!)
      .then((data) => {
        setContest(data)
        return contestsService.listApprovedArtworks(data.id)
      })
      .then((artworkData) => setArtworks(artworkData))
      .catch(() => {
        toast.error('Não foi possível carregar os detalhes do concurso')
        navigate('/contests')
      })
      .finally(() => setLoading(false))
  }, [slug])

  if (loading) {
    return <ContestDetailSkeleton />
  }

  if (!contest) {
    return null
  }

  const status = getContestStatus(contest)
  const hasLimit = contest.max_registrations > 0
  const fillPct =
    hasLimit && contest.max_registrations > 0
      ? Math.round((contest.paid_applications_count / contest.max_registrations) * 100)
      : 0
  const available = hasLimit ? Math.max(0, contest.max_registrations - contest.paid_applications_count) : null

  return (
    <>
      {/* Top Navigation Bar */}
      <Flex
        justify="space-between"
        align={{ base: 'flex-start', md: 'center' }}
        direction={{ base: 'column', md: 'row' }}
        gap={4}
        mb={5}
      >
        <BackLink to="/contests">Voltar para Concursos</BackLink>

        <SearchableSelect
          options={contestOptions.map((opt) => ({ value: opt.slug, label: opt.name }))}
          value={contest.slug}
          onChange={(val) => {
            if (val !== contest.slug) navigate(`/contests/${val}`)
          }}
          placeholder="Selecionar concurso..."
        />
      </Flex>
      <DetailHero
        kicker="Concurso"
        kickerColorScheme="pink"
        image={contest.event?.thumb_wide}
        title={contest.name}
        subtitle={
          <>
            Inscrições até {formatApiDate(contest.registration_deadline)}
            {' · '}
            Obras até {formatApiDate(contest.submission_deadline)}
          </>
        }
        badges={
          <>
            <StatusBadge
              label={status.label}
              colorScheme={status.colorScheme}
              py={0.5}
              textTransform="uppercase"
              letterSpacing="wider"
            />
            {contest.event && (
              <Badge
                as={Link}
                to={`/events/${contest.event.slug}`}
                colorScheme="purple"
                py={0.5}
                textTransform="uppercase"
                letterSpacing="wider"
                _hover={{ opacity: 0.8, textDecoration: 'none' }}
              >
                <Icon name="lni lni-calendar" mr="4px" />
                {contest.event.name}
              </Badge>
            )}
          </>
        }
        actions={
          <>
            <HeroLinkButton
              label={contest.link_type === 'exposicao' ? 'Página da exposição' : 'Página do concurso'}
              url={contestPublicUrl(contest)}
            />
            {contest.event && (
              <HeroLinkButton
                label="Página do evento"
                url={`${WEBSITE_URL}/eventos/${contest.event.slug}`}
                icon="lni lni-calendar"
              />
            )}
            {hasRole('view_contests_applications') && (
              <HeroButton icon="lni lni-users" to={`/contests/applications?contest=${contest.slug}`}>
                Ver Inscritos
              </HeroButton>
            )}
            <HeroButton
              icon="lni lni-download"
              isLoading={isDownloading}
              onClick={() =>
                download(() => contestsService.downloadParticipantsXlsx(contest.id), 'Erro ao exportar participantes')
              }
            >
              Exportar participantes
            </HeroButton>
            {hasRole(['evaluate_contest_artworks', 'view_contest_votes']) && (
              <HeroButton icon="lni lni-star-half" to={`/contests/artwork-evaluation/${contest.slug}`}>
                {hasRole('evaluate_contest_artworks') ? 'Avaliar obras' : 'Ver votações'}
              </HeroButton>
            )}
          </>
        }
      />

      {/* Stats mini-cards */}
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={6}>
        <StatCard
          icon="lni lni-users"
          label="Inscrições"
          value={`${contest.paid_applications_count}/${contest.max_registrations}`}
          helpText={hasLimit ? `${fillPct}% ocupado` : undefined}
          color="abraPurple.500"
        />
        <StatCard
          icon="lni lni-image"
          label="Obras aprovadas"
          value={contest.approved_arts_count}
          helpText="Total de obras aprovadas"
          color="blue.500"
        />
        <StatCard
          icon="lni lni-hammer"
          label="Obras em leilão"
          value={contest.arts_for_auction_count}
          helpText="Aprovadas para leilão"
          color="orange.500"
        />
      </SimpleGrid>
      {/* Revenue cards */}
      <RevenueSummaryCards contestId={contest.id} />
      {/* Daily registrations chart */}
      {contest.registrations_per_day.length > 0 && (
        <SectionCard title="Inscrições por dia" mb={6}>
          <Box maxH="220px" w="100%">
            <Bar
              height={220}
              data={{
                labels: contest.registrations_per_day.map((s) =>
                  new Date(s.date + 'T12:00:00').toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })
                ),
                datasets: [
                  {
                    label: 'Inscrições',
                    data: contest.registrations_per_day.map((s) => s.count),
                    backgroundColor: magenta[500],
                    borderRadius: 4,
                    maxBarThickness: 40
                  }
                ]
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: { legend: { display: false }, tooltip: { enabled: true } },
                scales: {
                  y: { beginAtZero: true, ticks: { stepSize: 1 } },
                  x: { grid: { display: false } }
                }
              }}
            />
          </Box>
        </SectionCard>
      )}
      {/* Main content */}
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6} mb={6}>
        {/* Left: fill gauge */}
        {hasLimit && (
          <GridItem>
            <SectionCard title="Ocupação de Vagas" h="100%">
              <Flex align="center" justify="center" gap={8} direction={{ base: 'column', md: 'row' }}>
                <Box position="relative">
                  <CircularProgress
                    value={fillPct}
                    size="160px"
                    thickness="10px"
                    color="magenta.500"
                    trackColor="gray.100"
                  >
                    <CircularProgressLabel>
                      <Text fontSize="2xl" fontWeight="800" color="gray.800" lineHeight="1">
                        {fillPct}%
                      </Text>
                      <Text variant="caption">ocupado</Text>
                    </CircularProgressLabel>
                  </CircularProgress>
                </Box>
                <VStack align="start" spacing={3}>
                  <Flex align="center" gap={2}>
                    <Box w="10px" h="10px" borderRadius="sm" bg="magenta.500" />
                    <Text variant="caption">
                      Pagas: <strong>{contest.paid_applications_count}</strong>
                    </Text>
                  </Flex>
                  <Flex align="center" gap={2}>
                    <Box w="10px" h="10px" borderRadius="sm" bg="gray.100" border="1px" borderColor="gray.300" />
                    <Text variant="caption">
                      Disponíveis: <strong>{available}</strong>
                    </Text>
                  </Flex>
                  <Divider />
                  <Text variant="caption">
                    Total: <strong>{contest.max_registrations}</strong> vagas
                  </Text>
                </VStack>
              </Flex>
            </SectionCard>
          </GridItem>
        )}

        {/* Right: dates & info */}
        <GridItem colSpan={hasLimit ? 1 : 2}>
          <SectionCard title="Informações do Concurso" h="100%">
            <VStack align="stretch" spacing={4}>
              <DetailField
                icon="lni lni-calendar"
                label="Prazo de inscrições"
                value={formatApiDate(contest.registration_deadline)}
              />
              <DetailField
                icon="lni lni-image"
                label="Prazo de envio de obras"
                value={formatApiDate(contest.submission_deadline)}
              />
              <DetailField
                icon="lni lni-package"
                label="Prazo de entrega"
                value={formatApiDate(contest.delivery_deadline)}
              />
              <DetailField
                icon="lni lni-flag"
                label="Data de abertura"
                value={formatApiDate(contest.opening_event_date)}
              />
              {(contest.withdraw_start_date || contest.withdraw_end_date) && (
                <DetailField
                  icon="lni lni-exit"
                  label="Período de retirada"
                  value={formatWithdrawWindow(contest.withdraw_start_date, contest.withdraw_end_date)}
                />
              )}
              {contest.registration_fee > 0 && (
                <DetailField
                  icon="lni lni-wallet"
                  label="Taxa de inscrição"
                  value={`${formatCurrency(contest.registration_fee)} (até ${contest.max_installments}x)`}
                />
              )}
            </VStack>
          </SectionCard>
        </GridItem>
      </Grid>
      {/* Courses */}
      {contest.courses.length > 0 && (
        <Box mb={6}>
          <SectionTitle>Cursos Vinculados</SectionTitle>
          <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
            {contest.courses.map((course) => (
              <SectionCard key={course.id} variant="tile">
                <Flex align="center" gap={3}>
                  <Flex
                    w="36px"
                    h="36px"
                    borderRadius="lg"
                    bg="abraPurple.50"
                    align="center"
                    justify="center"
                    flexShrink={0}
                  >
                    <Icon name="lni lni-graduation" size="lg" color="purple.600" />
                  </Flex>
                  <Box flex={1} minW={0}>
                    <Text fontSize="sm" fontWeight="600" color="gray.800" noOfLines={2}>
                      {course.name}
                    </Text>
                  </Box>
                  <Badge colorScheme={course.enabled ? 'green' : 'gray'} py={0.5} flexShrink={0}>
                    {course.enabled ? 'Ativo' : 'Inativo'}
                  </Badge>
                </Flex>
              </SectionCard>
            ))}
          </SimpleGrid>
        </Box>
      )}
      {/* Approved Artworks */}
      {artworks.length > 0 && (
        <Box mb={6}>
          <SectionTitle>Obras aprovadas ({artworks.length})</SectionTitle>
          <SimpleGrid columns={{ base: 1, sm: 2, md: 3, lg: 4 }} spacing={4}>
            {artworks.map((art) => (
              <ArtworkCard
                key={art.id}
                art={art}
                onImageClick={art.art_image_url ? () => openImagePreview(art.art_image_url!) : undefined}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}
      {/* Lightbox, não modal de formulário: o conteúdo é transparente e sem sombra, para a
          imagem flutuar sobre o overlay em vez de aparecer dentro de um cartão branco. */}
      <Modal isOpen={imageModal.isOpen} onClose={imageModal.onClose} size="4xl" isCentered>
        <ModalOverlay backdropFilter="blur(4px)" />
        <ModalContent bg="transparent" shadow="none">
          <ModalCloseButton color="white" zIndex={2} bg="blackAlpha.600" borderRadius="full" />
          <ModalBody p={0} display="flex" justifyContent="center" alignItems="center">
            {previewUrl && (
              <Image
                src={previewUrl}
                alt="Obra em tamanho completo"
                maxH="80vh"
                objectFit="contain"
                borderRadius="lg"
              />
            )}
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  )
}

/* ─── Sub-components ─────────────────────────────────────── */

const PLACEHOLDER_AVATAR = 'https://ui-avatars.com/api/?background=e2e8f0&color=718096&size=64'
/**
 * O vazio da imagem da obra é desenhado aqui, não buscado fora.
 *
 * Era um `via.placeholder.com`, serviço fora do ar desde 2024: o `fallbackSrc` também não
 * carregava, então a obra sem imagem ficava com o retângulo cinza vazio e uma requisição
 * quebrada no console.
 */
const ArtImageFallback = () => (
  <Flex h="100%" align="center" justify="center" direction="column" gap={1} color="gray.400">
    <Icon name="lni lni-image" size="xl" />
    <Text fontSize="sm">Sem imagem</Text>
  </Flex>
)

function ArtworkCard({ art, onImageClick }: { art: IContestApprovedArtwork; onImageClick?: () => void }) {
  const isAuction = art.available_for_auction
  const isSold = art.sale_status === 'sold'

  return (
    <SectionCard
      variant="flush"
      boxShadow="sm"
      _hover={{ boxShadow: 'md', transform: 'translateY(-2px)' }}
      transition="all 0.15s ease"
    >
      {/* Art image */}
      <Box
        position="relative"
        h="180px"
        bg="gray.50"
        overflow="hidden"
        cursor={onImageClick ? 'zoom-in' : 'default'}
        onClick={onImageClick}
      >
        <Image
          src={art.art_image_url ?? ''}
          alt={art.name}
          w="100%"
          h="100%"
          objectFit="cover"
          fallback={<ArtImageFallback />}
        />
        {/* Badges overlay */}
        <Flex position="absolute" top={2} right={2} gap={1} flexWrap="wrap" justify="flex-end">
          {isAuction && (
            <Badge colorScheme="orange" py={0.5}>
              Leilão
            </Badge>
          )}
          {isSold && (
            <Badge colorScheme="red" py={0.5}>
              Vendida
            </Badge>
          )}
        </Flex>
      </Box>

      {/* Card body */}
      <Box p={4}>
        {/* Author row */}
        <Flex align="center" gap={2} mb={3}>
          <Image
            src={art.applicant_image_url ?? PLACEHOLDER_AVATAR}
            alt={art.applicant_name ?? ''}
            w="32px"
            h="32px"
            borderRadius="full"
            objectFit="cover"
            flexShrink={0}
            fallbackSrc={PLACEHOLDER_AVATAR}
          />
          <Text variant="caption" noOfLines={1}>
            <EmptyCell value={art.applicant_name} />
          </Text>
        </Flex>

        {/* Art name */}
        <Text fontSize="sm" fontWeight="700" color="gray.800" mb={1} noOfLines={2} lineHeight="1.3">
          {art.name}
        </Text>

        {/* Customizable artwork data fields (ABRA-73) */}
        <ArtMetaInline meta={resolveArtMeta(art)} fontSize="xs" color="gray.400" noOfLines={1} />
      </Box>
    </SectionCard>
  )
}

function ContestDetailSkeleton() {
  return (
    <>
      <Skeleton height="16px" width="150px" mb={5} borderRadius="md" />
      <Skeleton height="120px" borderRadius="xl" mb={6} />
      <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} mb={6}>
        {[1, 2, 3, 4].map((i) => (
          <SectionCard key={i} variant="tile">
            <Skeleton height="12px" width="80px" mb={2} />
            <Skeleton height="28px" width="60px" />
          </SectionCard>
        ))}
      </SimpleGrid>
      <Skeleton height="240px" borderRadius="xl" mb={6} />
      <Grid templateColumns={{ base: '1fr', lg: '1fr 1fr' }} gap={6}>
        {[1, 2].map((i) => (
          <SectionCard key={i}>
            <Skeleton height="16px" width="180px" mb={5} />
            <VStack align="stretch" spacing={4}>
              {[1, 2, 3, 4].map((j) => (
                <Flex key={j} align="center" gap={3}>
                  <Skeleton height="32px" width="32px" borderRadius="lg" />
                  <Box flex={1}>
                    <Skeleton height="10px" width="50px" mb={1} />
                    <Skeleton height="14px" width="120px" />
                  </Box>
                </Flex>
              ))}
            </VStack>
          </SectionCard>
        ))}
      </Grid>
    </>
  )
}
